import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useIsCustomThemesEnabled } from "@/react/components/SettingsApp/Site/Pages/hooks/useIsCustomThemesEnabled";
import { SimpleDropdown } from "@circle-react/components/shared/uikit/SimpleDropdown";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { SidebarSubTitle } from "@circle-react-shared/BuilderUI/Sidebar/SidebarSubTitle";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { BadgeV2 } from "@circle-react-shared/uikit/BadgeV2";
import { useGetThemes } from "../useGetThemes";
import { SavedCustomThemeDropdownOptions } from "./SavedCustomThemeDropdownOptions";
import { ThemeOptionLabel } from "./ThemeOptionLabel";
import { UnsavedCustomThemeDropdown } from "./UnsavedCustomThemeDropdown";

export const ThemeSelector = () => {
  const appearance = useBuilderStore(state => state.appearance);
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);
  const toggleCurrentCanvasTheme = useBuilderStore(
    state => state.toggleCurrentCanvasTheme,
  );
  const isCustomThemesEnabled = useIsCustomThemesEnabled();

  const {
    formState: { isDirty },
    reset,
  } = useFormContext();

  const { data: themes, isLoading } = useGetThemes();

  const options =
    themes?.map(theme => ({
      id: theme.value,
      value: theme.value,
      label: (
        <ThemeOptionLabel
          key={theme.value}
          theme={theme}
          appearance={appearance}
        />
      ),
    })) || [];

  const handleThemeChange = (themeValue: string | number) => {
    const selectedTheme = themes?.find(theme => theme.value === themeValue);

    if (selectedTheme) {
      toggleCurrentCanvasTheme({
        ...selectedTheme,
        customColors: currentCanvasTheme.customColors,
      });
      reset({
        theme: selectedTheme.cssVariables,
      });
    }
  };

  return (
    <div className="flex flex-col gap-2 px-6 py-5">
      <div className="flex items-center justify-between">
        <SidebarSubTitle>
          {t("builder_ui.theme_configuration.site_themes.current_theme.title")}
        </SidebarSubTitle>
        {isCustomThemesEnabled && (
          <div className="flex items-center gap-2">
            {isDirty && (
              <div className="flex items-center gap-2">
                <BadgeV2
                  label={t("builder_ui.theme_configuration.modified")}
                  square
                  className="!bg-v2-warning-100 !text-dark"
                />
                <UnsavedCustomThemeDropdown />
              </div>
            )}
            {!isDirty && currentCanvasTheme.isCustom && (
              <SavedCustomThemeDropdownOptions />
            )}
          </div>
        )}
      </div>
      {isLoading ? (
        <LoaderRow />
      ) : (
        <SimpleDropdown
          className="!flex"
          value={currentCanvasTheme.value}
          onChange={handleThemeChange}
          menuItems={options}
          fullWidth
        />
      )}
    </div>
  );
};
